import React, { useId } from "react";

/**
 * ProgressRing — a circular SVG progress ring with a teal→violet gradient
 * stroke. Used inside the Overview hero card to visualize the average health
 * score as a percentage.
 *
 * Props:
 *  - value:   0-100 (required)
 *  - size:    outer diameter in px (default 140)
 *  - stroke:  ring thickness in px (default 12)
 *  - trackColor: colour of the unfilled ring
 *  - children: whatever should render inside the ring (usually the number)
 */
export const ProgressRing = ({
  value = 0,
  size = 140,
  stroke = 12,
  trackColor = "rgba(255,255,255,0.12)",
  children,
  className = "",
  testId,
}) => {
  const clamped = Math.max(0, Math.min(100, value));
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const dash = (clamped / 100) * circ;
  const gid = `ring-grad-${useId().replace(/:/g, "")}`;

  return (
    <div
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: size, height: size }}
      data-testid={testId}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00B4D8" />
            <stop offset="100%" stopColor="#7C6FF0" />
          </linearGradient>
        </defs>
        {/* Track */}
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={stroke}
        />
        {/* Value arc */}
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none"
          stroke={`url(#${gid})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dasharray 900ms cubic-bezier(0.22, 1, 0.36, 1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-center">
        {children}
      </div>
    </div>
  );
};

export default ProgressRing;
